import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Divider,
  List,
  ListItem,
  IconButton,
  Stack,
  Alert,
} from "@mui/material";
import {
  MdBackup,
  MdRefresh,
  MdDownload,
  MdDelete,
  MdFolderZip,
  MdLockOutline,
} from "react-icons/md";
import axios from "axios";
import config from "../../src/config";
// 1. IMPORT YOUR NEW COLOR CONFIG
import { Colors } from "../config/themeConfig";

const baseUrl = config.baseURL;

const BackupManager = ({ permissions = { backup: { download: true } } }) => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [customFilename, setCustomFilename] = useState("");
  const [error, setError] = useState(null);

  // --- UTILS ---
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatBackupDate = (filename) => {
    const timestamp = filename.match(/\d{13}/);
    if (timestamp) {
      const date = new Date(parseInt(timestamp[0]));
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date);
    }
    return "Date unknown";
  };

  const callApi = async (
    url,
    data = null,
    method = "GET",
    responseType = "json"
  ) => {
    const token = localStorage.getItem("token");
    return axios({
      url: `${baseUrl}${url}`,
      method,
      data,
      responseType,
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  // --- ACTIONS ---
  const fetchBackups = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await callApi("/api/backups");
      setBackups(res.data.backups || []);
    } catch (err) {
      setError("Failed to fetch backups.");
    }
    setLoading(false);
  };

  const createBackup = async () => {
    if (!customFilename) return;
    setCreatingBackup(true);
    setError(null);
    try {
      const res = await callApi(
        "/api/backup-company",
        { label: customFilename },
        "POST",
        "blob"
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${customFilename.replace(/\s+/g, "_")}.zip`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setCustomFilename("");
      fetchBackups();
    } catch (err) {
      setError("Backup generation failed.");
    }
    setCreatingBackup(false);
  };

  const downloadBackup = async (file) => {
    setActionLoading(file.name);
    setError(null);
    try {
      const res = await callApi(
        `/api/backup/download/${file.name}`,
        null,
        "GET",
        "blob"
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Download failed.");
    }
    setActionLoading(null);
  };

  const deleteBackup = async (file) => {
    if (!window.confirm(`Permanently delete ${file.name}?`)) return;
    setActionLoading(file.name);
    setError(null);
    try {
      await callApi(`/api/backup/${file.name}`, null, "DELETE");
      fetchBackups();
    } catch (err) {
      setError("Delete failed.");
    }
    setActionLoading(null);
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: Colors.brand.background,
        minHeight: "100vh",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 1000,
          mx: "auto",
          borderRadius: "16px",
          border: `1px solid ${Colors.brand.border}`,
          overflow: "hidden",
        }}
      >
        {/* HEADER SECTION */}
        <Box sx={{ p: 4, backgroundColor: Colors.brand.surface }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: Colors.brand.primary,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {/* ICON USES PRIMARY BRAND COLOR */}
                <MdBackup color={Colors.brand.primary} size={32} /> Backup
                Management
              </Typography>
              <Typography variant="body2" sx={{ color: Colors.text.secondary }}>
                Monitor and manage historical data snapshots.
              </Typography>
            </Box>
            <IconButton
              onClick={fetchBackups}
              sx={{
                border: `1px solid ${Colors.brand.border}`,
                color: Colors.text.secondary,
              }}
            >
              <MdRefresh />
            </IconButton>
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{ mt: 3, borderRadius: "8px", color: Colors.status.error }}
            >
              {error}
            </Alert>
          )}

          {/* GENERATE ACTION BAR */}
          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 2,
              p: 2,
              borderRadius: "12px",
              backgroundColor: Colors.brand.background, // Subtle contrast against white paper
            }}
          >
            <TextField
              label="Backup Label"
              size="small"
              fullWidth
              sx={{
                backgroundColor: Colors.brand.surface,
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
              value={customFilename}
              onChange={(e) => setCustomFilename(e.target.value)}
            />
            <Button
              variant="contained"
              onClick={createBackup}
              disabled={creatingBackup || !customFilename}
              startIcon={
                creatingBackup ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <MdBackup />
                )
              }
              sx={{
                px: 4,
                backgroundColor: Colors.brand.accent, // USES TEAL ACCENT
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#16A085" }, // Slightly darker teal
              }}
            >
              Generate
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* LIST SECTION */}
        <Box sx={{ minHeight: 400, backgroundColor: Colors.brand.surface }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
              <CircularProgress sx={{ color: Colors.brand.primary }} />
            </Box>
          ) : (
            <List disablePadding>
              {backups.map((b) => (
                <ListItem
                  key={b.name}
                  divider
                  sx={{
                    px: 4,
                    py: 3,
                    "&:hover": { backgroundColor: Colors.brand.background },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    sx={{ width: "100%" }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        backgroundColor: Colors.brand.background,
                        color: Colors.brand.primary,
                      }}
                    >
                      <MdFolderZip size={30} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        sx={{ fontWeight: 700, color: Colors.text.main }}
                      >
                        {b.name}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{ color: Colors.text.secondary }}
                        >
                          <strong>Date:</strong> {formatBackupDate(b.name)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: Colors.text.secondary }}
                        >
                          <strong>Size:</strong> {formatFileSize(b.size)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: Colors.status.success, // USES EMERALD SUCCESS COLOR
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <MdLockOutline size={12} /> SECURE
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={actionLoading === b.name}
                        startIcon={<MdDownload />}
                        onClick={() => downloadBackup(b)}
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 700,
                          textTransform: "none",
                          color: Colors.brand.accent, // ACCENT COLOR
                          borderColor: Colors.brand.border,
                          "&:hover": {
                            borderColor: Colors.brand.accent,
                            backgroundColor: "#E0F2F1",
                          },
                        }}
                      >
                        Download
                      </Button>
                      <IconButton
                        onClick={() => deleteBackup(b)}
                        sx={{
                          border: `1px solid ${Colors.brand.border}`,
                          color: Colors.text.secondary,
                          "&:hover": {
                            color: Colors.status.error,
                            backgroundColor: "#FDEDEC",
                          },
                        }}
                      >
                        <MdDelete size={20} />
                      </IconButton>
                    </Stack>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default BackupManager;
